import { useQuery } from "@apollo/client";
import { compact, sortBy } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  AssessmentGroupAnonymity,
  AssessmentState,
  AssessmentSubmissionNodeType,
  AssessmentType,
  ComplianceProgramParticipantStatus,
  GetReviewSubmissionsQuery,
  GetReviewSubmissionsQueryVariables,
  RelatedAssessmentSubmissionFragment,
} from "types/graphql-schema";
import { BasicUser, TFLocationState } from "types/topicflow";

import getReviewSubmissionsQuery from "@apps/assessments/graphql/get-review-submissions-query";
import useLabel, { UseLabelType } from "@apps/use-label/use-label";
import { currentUserVar } from "@cache/cache";
import Avatars from "@components/avatar/avatars";
import Button, { buttonTheme } from "@components/button/button";
import Layout from "@components/layout/layout";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import StatusPill, {
  StatusPillEnum,
} from "@components/status-pill/status-pill";
import Tooltip from "@components/tooltip/tooltip";
import {
  TopicflowIconMap,
  getTopicflowIcon,
} from "@components/topicflow-icon/topicflow-icon";
import { assertNonNull, toWithBackground } from "@helpers/helpers";

const submissionSort = {
  [AssessmentSubmissionNodeType.PeerNominations]: 1,
  [AssessmentSubmissionNodeType.SelfAssessment]: 2,
  [AssessmentSubmissionNodeType.PeerAssessment]: 3,
  [AssessmentSubmissionNodeType.UpwardAssessment]: 4,
  [AssessmentSubmissionNodeType.ManagerAssessment]: 5,
  [AssessmentSubmissionNodeType.Calibration]: 6,
  [AssessmentSubmissionNodeType.Delivery]: 7,
};

const submissionLabels = (label: UseLabelType) => ({
  [AssessmentSubmissionNodeType.PeerNominations]: "Peer Nominations",
  [AssessmentSubmissionNodeType.SelfAssessment]: `Self ${label("review", {
    capitalize: true,
  })}`,
  [AssessmentSubmissionNodeType.ManagerAssessment]: `Downward ${label(
    "review",
    {
      capitalize: true,
    }
  )}`,
  [AssessmentSubmissionNodeType.PeerAssessment]: `Peer ${label("review", {
    capitalize: true,
    pluralize: true,
  })}`,
  [AssessmentSubmissionNodeType.UpwardAssessment]: `Upward ${label("review", {
    capitalize: true,
    pluralize: true,
  })}`,
  [AssessmentSubmissionNodeType.Calibration]: "Calibration",
  [AssessmentSubmissionNodeType.Delivery]: "Share with Subject",
});

const getSidebarLink = (submission: RelatedAssessmentSubmissionFragment) => {
  if (
    [
      AssessmentSubmissionNodeType.UpwardAssessment,
      AssessmentSubmissionNodeType.PeerAssessment,
    ].includes(submission.type) &&
    submission.deliveryId
  ) {
    return `/assessments/assessment/delivery/${submission.deliveryId}`;
  }
  return null;
};

const RelatedSubmissionStatusPill = ({
  submission,
}: {
  submission: RelatedAssessmentSubmissionFragment;
}) => {
  const statusLabel = useMemo(() => {
    if (submission.type === AssessmentSubmissionNodeType.PeerNominations) {
      return `${submission.completeCount} Nominated`;
    } else if (submission.type === AssessmentSubmissionNodeType.Calibration) {
      return submission.status === ComplianceProgramParticipantStatus.Complete
        ? "Calibrated"
        : "Not Calibrated";
    } else if (submission.type === AssessmentSubmissionNodeType.Delivery) {
      return submission.status === ComplianceProgramParticipantStatus.Complete
        ? "Shared"
        : submission.status === ComplianceProgramParticipantStatus.InProgress
        ? "Approved"
        : "Not Shared";
    } else if (
      [
        AssessmentSubmissionNodeType.SelfAssessment,
        AssessmentSubmissionNodeType.ManagerAssessment,
      ].includes(submission.type)
    ) {
      if (submission.status === ComplianceProgramParticipantStatus.Complete) {
        return "Complete";
      }
      return submission.pendingCount > 0 ? "In Progress" : "N/A";
    } else {
      return submission.completeCount + submission.pendingCount > 0
        ? `${submission.completeCount}/${
            submission.completeCount + submission.pendingCount
          } Complete`
        : "N/A";
    }
  }, [submission]);
  return (
    <StatusPill
      label={statusLabel}
      status={
        submission.status === ComplianceProgramParticipantStatus.Complete
          ? StatusPillEnum.Complete
          : submission.status === ComplianceProgramParticipantStatus.InProgress
          ? StatusPillEnum.InProgress
          : StatusPillEnum.NotApplicable
      }
    />
  );
};

const RelatedSubmissionTypeItem = ({
  submission,
  target,
  showButtonToScroll,
  deliveryLink,
  leaveSpaceForDeliveryLink,
  leaveSpaceForScrollButton,
}: {
  submission: RelatedAssessmentSubmissionFragment;
  target: BasicUser;
  showButtonToScroll: boolean;
  leaveSpaceForScrollButton: boolean;
  deliveryLink: string | null;
  leaveSpaceForDeliveryLink: boolean;
}) => {
  const label = useLabel();
  const currentUser = currentUserVar();
  const location = useLocation<TFLocationState>();
  const currentUserIsTarget = currentUser.id === target.id;

  const handleClickScroll = useCallback(() => {
    document
      .getElementById(
        `container-assessment-type-${submission.template?.assessmentType}`
      )
      ?.scrollIntoView({ behavior: "smooth" });
  }, [submission.template?.assessmentType]);

  return (
    <Layout.MainSubSectionListItem>
      <div className="flex items-center gap-3">
        <div className="w-48 text-gray-500">
          {submissionLabels(label)[submission.type]}
        </div>
        <RelatedSubmissionStatusPill submission={submission} />
        <div className="flex-1 flex justify-end gap-3 items-center">
          {(submission.template?.anonymity ===
            AssessmentGroupAnonymity.Anonymous ||
            (currentUserIsTarget &&
              submission.template?.anonymity ===
                AssessmentGroupAnonymity.SemiAnonymous)) && (
            <Tooltip
              text={`Responders are anonymous for this ${label("review")}.`}
            >
              <div className="text-gray-400 text-sm">
                {submission.template.anonymity ===
                AssessmentGroupAnonymity.Anonymous
                  ? "Anonymous"
                  : "Semi-Anonymous"}
              </div>
            </Tooltip>
          )}
          {submission.users.length === 0 ? null : (
            <Avatars
              users={submission.users}
              max={5}
              size={6}
              modalTitle="Responders"
            />
          )}
        </div>

        {deliveryLink && (
          <Button
            to={
              deliveryLink
                ? toWithBackground({
                    pathname: deliveryLink,
                    location,
                  })
                : ""
            }
            theme={buttonTheme.iconGray}
            icon={getTopicflowIcon("arrowRight")}
          />
        )}
        {!deliveryLink && leaveSpaceForDeliveryLink && (
          <div className="w-[32px] shrink-0"></div>
        )}

        {showButtonToScroll && (
          <Button
            theme={buttonTheme.iconGray}
            icon={TopicflowIconMap.chevronDownDouble}
            onClick={handleClickScroll}
          />
        )}
        {!showButtonToScroll && leaveSpaceForScrollButton && (
          <div className="w-[32px] shrink-0"></div>
        )}
      </div>
    </Layout.MainSubSectionListItem>
  );
};

// we only want to warn when the self assessment or peer assessment is incomplete
const filterManagerAndCompletedSubmissions = (
  submission: {
    type: AssessmentSubmissionNodeType;
    completeCount: number;
    pendingCount: number;
  } | null
) =>
  submission &&
  ((submission.type === AssessmentSubmissionNodeType.SelfAssessment &&
    submission.pendingCount > 0) ||
    (submission.type === AssessmentSubmissionNodeType.PeerNominations &&
      submission.completeCount === 0) ||
    (submission.type === AssessmentSubmissionNodeType.PeerAssessment &&
      submission.pendingCount > 0));

const RelatedSubmissions = ({
  target,
  complianceProgramId,
  assessmentState,
  assessmentType,
}: {
  target: BasicUser;
  complianceProgramId: number;
  assessmentType?: AssessmentType;
  assessmentState?: AssessmentState;
}) => {
  const label = useLabel();
  const currentUser = currentUserVar();
  const [showWarningModal, setShowWarningModal] = useState(false);
  const isCurrentUserTarget = currentUser?.id === target.id;

  const { data, loading } = useQuery<
    GetReviewSubmissionsQuery,
    GetReviewSubmissionsQueryVariables
  >(getReviewSubmissionsQuery, {
    variables: {
      targetId: target.id,
      complianceProgramId,
    },
    onCompleted: (response) => {
      if (
        assessmentState === AssessmentState.Draft &&
        assessmentType === AssessmentType.Performance &&
        !isCurrentUserTarget &&
        response.complianceProgram?.assessmentSubmissions &&
        response.complianceProgram?.assessmentSubmissions.filter(
          filterManagerAndCompletedSubmissions
        ).length > 0
      ) {
        setShowWarningModal(true);
      }
    },
  });

  const submissionRows = useMemo(() => {
    const complianceProgram = data?.complianceProgram;
    const submissions = compact(complianceProgram?.assessmentSubmissions);
    return sortBy(submissions, (submission) => submissionSort[submission.type]);
  }, [data]);
  const submissionsWithExtraData = submissionRows.map((submission) => ({
    ...submission,
    showScrollToButton: !!document.getElementById(
      `container-assessment-type-${submission.template?.assessmentType}`
    ),
    sidebarLink:
      assessmentState === AssessmentState.Draft &&
      assessmentType === AssessmentType.Performance
        ? getSidebarLink(submission)
        : null,
  }));

  const someHaveScrollButtons = submissionsWithExtraData.some(
    (submission) => submission.showScrollToButton
  );
  const someHaveDeliveryLinks = submissionsWithExtraData.some(
    (submission) => submission.sidebarLink
  );

  return (
    <>
      <Modal open={showWarningModal} onClose={() => setShowWarningModal(false)}>
        <div className="flex flex-col p-6">
          <ModalTitle className="mb-3">{`Some ${label("review", {
            capitalize: true,
            pluralize: true,
          })} Aren't Complete`}</ModalTitle>
          <div className="mb-6">
            We often recommend Manager's complete their {label("review")} after
            these {label("review", { pluralize: true })} (shown below) are
            complete. Did you want to start working on this {label("review")}{" "}
            anyway?
          </div>
          <div className="text-xs font-medium mb-4">{`${label("review", {
            capitalize: true,
          })} Submissions`}</div>
          <div className="border rounded-md mb-6 flex flex-col divide-y">
            {submissionsWithExtraData
              ?.filter(filterManagerAndCompletedSubmissions)
              .map(
                (submission) =>
                  submission && (
                    <div
                      key={`${submission.type}}`}
                      className="flex items-center justify-between py-3 px-4"
                    >
                      <div className="w-36 text-gray-500">
                        {submissionLabels(label)[submission.type]}
                      </div>
                      <RelatedSubmissionStatusPill submission={submission} />
                      <Avatars
                        users={submission.users}
                        max={5}
                        size={6}
                        modalTitle="Responders"
                      />
                    </div>
                  )
              )}
          </div>

          <div className="flex items-center justify-end gap-4">
            <Button url="/assessments" text="I'll Come Back Later" />
            <Button
              onClick={() => setShowWarningModal(false)}
              theme={buttonTheme.primary}
              text={`Continue to ${label("review", { capitalize: true })}`}
            />
          </div>
        </div>
      </Modal>
      <Layout.MainSubSection
        className="mt-6"
        title={`${label("review", {
          capitalize: true,
        })} Submissions`}
        collapsible
        defaultIsExpanded
        loading={loading}
      >
        {!loading ? (
          <Layout.MainSubSectionList>
            {submissionsWithExtraData?.map((submission) => (
              <RelatedSubmissionTypeItem
                key={`${submission?.type}`}
                submission={assertNonNull(submission)}
                target={target}
                showButtonToScroll={submission.showScrollToButton}
                deliveryLink={submission.sidebarLink}
                leaveSpaceForDeliveryLink={someHaveDeliveryLinks}
                leaveSpaceForScrollButton={someHaveScrollButtons}
              />
            ))}
          </Layout.MainSubSectionList>
        ) : null}
      </Layout.MainSubSection>
    </>
  );
};

export default RelatedSubmissions;
